<template>
    <div>
        <DataTable 
            :value="denunciasRecibidas"
            :lazy="true"
            :loading="loading"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            :paginator="true"
            :totalRecords="totalRows"
            :first="offset"
            :rows="rows"
            @page="handlePageChange"
            class="p-datatable-forms col-12" 
            dataKey="id"
            :sortField="sortField"
            :sortOrder="sortOrder"
            @sort="handleSort"
        >
            <template #empty> No hay denuncias recibidas para mostrar. </template>
            <template #loading> Descargando, espere por favor. </template>

            <Column 
                v-for="column in keyShow" 
                :field="column.title" 
                :key="column.title" 
                :sortable="column.sortable"
                :sortField="column.fieldName"
            >
                <template #header>
                    <div class="flex align-items-center">
                        {{ column.title }}
                    </div>
                </template>
                <template #body="slotProps">
                    <div class="flex align-items-center">
                        <div class="flex mr-2">
                            <Button v-if="booleans(slotProps, 'triangleButton', column.title)" v-tooltip.left="slotProps.data.Numero
                                ? 'No fue asignado ningún propietario'
                                : 'Falta numero de sumario'
                                " icon="pi pi-exclamation-triangle" class="p-button-rounded p-button-danger" />
                        </div>
                        <div :class="getClass(column, slotProps.data)">
                            {{ formatDoc(column.title, slotProps.data) }}
                        </div>
                    </div>
                </template>
            </Column>
            <Column class="flex-row">
                <template #body="slotProps">
                    <div class="flex" style="text-align: right">
                        <div class="flex mr-2" v-if="isOfflineDoc(slotProps.data) && $offline.onlineMode">
                            <Button v-tooltip.left="'Subir'" icon="pi pi-upload"
                                class="p-button-rounded p-button-success p-button-outlined"
                                @click="subirActuacion(slotProps)" />
                        </div>
                        <div v-if="booleans(slotProps, 'numeroExtracto')">
                            <Button v-tooltip.left="'Recientemente asignada'" label="Extracto de Denuncia"
                                icon="pi pi-check" class="p-button-danger p-button-sm" @click="OpenModalAddNsum(slotProps)"
                                :style="{
                                    height: '40px ',
                                    width: '200px',
                                    marginRight: '0.5rem ',
                                }" />
                        </div>
                    </div>
                </template>
            </Column>
            <Column header="Acción" style="width: 150px">
                <template #body="slotProps">
                    <Button 
                        class="custom-action-button"
                        @click="aceptarActuacion(slotProps.data)"
                    >
                        <div>
                            <ShieldCheck
                                color="white"
                                :size="20"
                            />
                        </div>
                        Aceptar
                    </Button>
                </template>
            </Column>
            <template #paginatorstart>
                <div class="flex align-items-center">
                    <b>
                        <span class="pagination">
                            {{  totalRows 
                                ? `Mostrando registros del ${offset + 1} al ${Math.min(offset + getLimitValue, totalRows)} de un total de ${totalRows}` 
                                : ''
                            }}
                        </span>
                    </b>
                </div>
            </template>
            <template #paginatorend />
        </DataTable>
        <ModalTemplate
            title="Asignar datos requeridos"
            :showIcon="false"
            :closable="false"
            ref="modalDatosFaltantes"
        >
            <div class="flex flex-column gap-4 w-full max-w-sm text-sm text-gray-800">
                <!-- Secretario -->
                <div class="flex flex-column gap-1">
                    <label for="secretario" class="font-medium">Seleccione un secretario</label>
                    <Dropdown
                        id="secretario"
                        placeholder="Ingrese el secretario"
                        class="border border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                        :options="secretaryOptions"
                        optionLabel="apellido_nombre"
                        dataKey="id" 
                        v-model="datosFaltantes.selectedSecretary"
                    />
                </div>
                <!-- Número de sumario -->
                <div class="flex flex-column gap-1">
                    <label for="sumario" class="font-medium">Ingrese el número de sumario</label>
                    <InputText
                        id="sumario"
                        v-model="datosFaltantes.actuacionNumero"
                        type="text"
                        placeholder="Ingrese el sumario"
                        class="border border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                    />
                </div>

                <!-- Año -->
                <div class="flex flex-column gap-1">
                    <label for="anio" class="font-medium">Ingrese Año</label>
                    <Dropdown
                        id="year"
                        placeholder="Ingrese el año"
                        class="border border-blue-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                        :options="yearOptions"
                        optionLabel="year"
                        optionValue="value"
                        v-model="datosFaltantes.selectedYear"
                    />
                </div>
                <div class="flex justify-content-end gap-2">
                    <Button
                        label="Cancelar"
                        :disabled="loadingAsignar"
                        class="cancelar-button"
                        @click="closeModal(modalDatosFaltantes)"
                    />
                    <Button
                        :label="loadingAsignar ? 'Asignando...' : 'Asignar'"
                        :disabled="!datosValidos || loadingAsignar"
                        :icon="loadingAsignar ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
                        class="custom-action-button"
                        @click="handleSendDatosFaltantes"
                    />
                </div>
            </div>
        </ModalTemplate>
        <ModalTemplate
            :showIcon="false"
            :closable="true"
            ref="modalSuccess"
        >
            <div class="flex flex-column gap-4 w-full justify-content-center">
                <div class="w-full flex justify-content-center">
                    <UserRoundCheck color="#268C2D" :size="100"/>
                </div>
                <div class="w-full flex flex-column justify-content-center align-items-center">
                    <h3 class="w-full text-center">La actuación fue asignada correctamente</h3>
                    <div class="w-2 mb-3 blue-line"></div>
                    <div class="flex justify-content-center">
                        <span class="w-9 text-center">
                            Ahora se encuentra disponible en el listado de 'Mis actuaciones' y ya no estará en 'Actuaciones recibidas'."
                        </span>
                    </div>
                </div>
            </div>
        </ModalTemplate>
    </div>
</template>

<script>
import { computed, defineComponent, onMounted, ref, watch} from 'vue';
import SplitButton from "primevue/splitbutton";
import { ShieldCheck, UserRoundCheck  } from 'lucide-vue';
import {
    useRest,
    useStoreCompat
} from "@/composables/index.js";
import ModalTemplate from '@/components/ModalTemplate.vue';
import Dropdown from 'primevue/dropdown/Dropdown';

export default defineComponent({
    name: 'ActuacionesRecibidas',
    components: {
        SplitButton,
        ModalTemplate,
        ShieldCheck,
        UserRoundCheck
    },
    props:{
        getClass: Function,
        formatDoc: Function,
        booleans: Function,
        isOfflineDoc: Function,
        subirActuacion: Function,
        OpenModalAddNsum: Function,
        visibleButtons: Function,
        getItems: Function,
        vincularActuacion: Function,
        getActuacionesUserByStationId: Function
    },
    setup({
        formatDoc,
        booleans,
        isOfflineDoc,
        subirActuacion,
        OpenModalAddNsum,
        visibleButtons,
        getItems,
        vincularActuacion,
        getActuacionesUserByStationId
    }) {
        const rest = useRest()
        const store = useStoreCompat()

        const denunciasRecibidas = ref([])
        const loading = ref(true) 
        const totalRows = ref(0)
        const offset = ref(0)
        const rows = ref(10) 
        const page = ref(1)
        const sortField = ref(null)
        const sortOrder = ref(-1) 
        const modalDatosFaltantes = ref(null)
        const modalSuccess = ref(null)
        const yearOptions = ref([
            {year: new Date().getFullYear(), value: JSON.stringify(new Date().getFullYear()).substr(-2)},
            {year: new Date().getFullYear()-1, value: JSON.stringify(new Date().getFullYear()-1).substr(-2)},
        ])
        const secretaryOptions = ref(null)

        const datosFaltantes = ref({
            selectedYear: null,
            selectedSecretary: null,
            actuacionNumero: null
        })

        const datosActuacion = ref({})
        const loadingAsignar = ref(false);
        const datosValidos = computed(() => {
            return (
                datosFaltantes.value.selectedSecretary &&
                datosFaltantes.value.selectedYear &&
                datosFaltantes.value.actuacionNumero &&
                datosFaltantes.value.actuacionNumero.trim() !== ''
            );
        });
        
        const user = computed(() => store.state.user)
        const keyShow = ref([
            { title: "Fecha de Creación", fieldName: "FechaCreacion", sortable: true },
            { title: "N° de Actuacion", fieldName: "Numero", sortable: true },
            { title: "Actuaciones", fieldName: "TiposumarioID", sortable: false },
            { title: "Caratula/Causa", fieldName: "CausaCaratula", sortable: false },
            { title: "Juzgado", fieldName: "nombreJuzgado", sortable: false },
            { title: "Dependencia", fieldName: "Dependencia", sortable: false },
            { title: "Estado", fieldName: "EstadoSumario", sortable: false},
        ])

        const getLimitValue = computed(() => rows.value);

        const getDenunciasRecibidas = async () => {
            loading.value = true
            const limit = 10
            const offsetParam = offset.value || 0
            const search = {
                // "exp_pre_Actuacion.Numero": [{ operator: "=", value: null }],
                // "exp_pre_ActuacionUsuarioDependencia.UsuarioID": [{ operator: "=", value: 'Sin Usuario Asignado' }]
            };
            let url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/sumario`;

            let getOptions = {
                url,
                params: {
                    search: search,
                    limit: limit,
                    offset: offsetParam,
                    order: sortField.value
                        ? `${sortField.value} ${sortOrder.value === 1 ? 'ASC' : 'DESC'}`
                        : null,
                    getTotal: true,
                    actuacionesRecibidas : true
                }
            }

            try {
                const { data } = await rest.get(getOptions)
                totalRows.value = data.allrows
                denunciasRecibidas.value = data.data;
            } catch (error) {
                console.log(error);
            }finally{
                loading.value = false
            }
        }

        const handleSort = async (e) => {
            sortField.value = e.sortField
            sortOrder.value = e.sortOrder
            await getDenunciasRecibidas()
        }

        const handlePageChange = async (event) => {
            offset.value = event.first;
            rows.value = event.rows;
            page.value = event.page + 1;
            loading.value = true;
            try {
                await getDenunciasRecibidas()
            } finally {
                loading.value = false;
            }
        }

        const getClass = (column, sumario) => {
            if (column.title == "Estado") {
                switch (sumario.Estado) {
                case "En Curso":
                    return "bg-green-200 min-w-full flex justify-content-center white-space-nowrap p-1 font-medium text-green-800 border-round";
                case "Vencida":
                    return "bg-yellow-200 min-w-full flex justify-content-center white-space-nowrap p-1 font-medium text-yellow-800 border-round";
                case "Finalizada":
                    return "custom-bg-red-200 min-w-full flex justify-content-center white-space-nowrap p-1 font-medium custom-text-red-800 border-round";
                }
            }
        }
        const completeNullLabelsAndAddingID = (originalArray, labelProp) => {
            let arrayCopy = [];
            for (let i = 0; i < originalArray.length; i++) {
                arrayCopy.push({ ...originalArray[i] });
                arrayCopy[i].id = i;
                if (arrayCopy[i][labelProp] === null) {
                    arrayCopy[i][labelProp] = "sin datos";
                }
            }
            return arrayCopy;
        }

        const aceptarActuacion = (data) => {
            datosActuacion.value = {
                ActuacionID: data.ActuacionID,
                DenunciaID: data.DenunciaID,
                DependenciaID: data.DependenciaID,
                FechaCreacion: data.FechaCreacion,
                TiposumarioID: data.TiposumarioID,
                ActuacionUsuarioDependencia: data.ActuacionUsuarioDependencia.filter((item) => item.UsuarioID === 'Sin Usuario Asignado')
            }
            secretaryOptions.value = completeNullLabelsAndAddingID(getActuacionesUserByStationId(user.value?.userdata?.dependenciaID),'apellido_nombre')
            modalDatosFaltantes.value?.open()
        }


        const handleSendDatosFaltantes = async () => {
            loadingAsignar.value = true;
            try {
                //Asiganación Número Actuación
                const numeroOptions = {
                    url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/sumario/${datosActuacion.value.ActuacionID}`,
                    formData: {
                        ...datosActuacion.value, 
                        Numero:  `${datosFaltantes.value.actuacionNumero}/${datosFaltantes.value.selectedYear}`
                    },
                    params: { method: "methodCommon" }
                }
                await rest.put(numeroOptions)

                //Asiganación Secretario               
                let fechaYhoraActual = new Date();
                let fecha = fechaYhoraActual.toISOString().slice(0, 10).replace("T", " ");
                let dia = new Date(fechaYhoraActual);
                let hora = dia.getHours() + ":" + dia.getMinutes() + ":" + dia.getSeconds();
                fechaYhoraActual = fecha + " " + hora;

                const newSecretary = {
                    ActuacionUsuarioDependenciaID: datosActuacion.value?.ActuacionUsuarioDependencia[0].ActuacionUsuarioDependenciaID,
                    Fechadesde: fechaYhoraActual, 
                    Fechahasta: null, 
                    SumarioID: datosActuacion.value.ActuacionID,
                    UsuarioID: datosFaltantes.value.selectedSecretary.UsuarioID,   
                    DependenciaID: datosActuacion.value.DependenciaID, 
                }

                await rest.put({
                    url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/actuacion-usuario-dependencia/${datosActuacion.value?.ActuacionUsuarioDependencia[0].ActuacionUsuarioDependenciaID}`,
                    formData: newSecretary,
                    params: { method: "own" }
                    
                })
            } catch (error) {
                console.log(error);
            } finally {
                loadingAsignar.value = false;
                modalDatosFaltantes.value?.close()
                cleanData()
                openModalSuccess()
                await getDenunciasRecibidas()
            }
        }

        const openModalSuccess = () => {
            modalSuccess.value?.open()
        }

        const closeModal = (modal) => {
            if(modal.value){
                modal.value.close()
            }else{
                modal.close()
            }         
            cleanData()
        }

        const cleanData = () => {
            datosActuacion.value = {}
            datosFaltantes.value = {
                selectedYear: null,
                selectedSecretary: null,
                actuacionNumero: null
            }
        } 


        onMounted(async () => {
            await getDenunciasRecibidas()
        })

        return {
            //refs
            denunciasRecibidas,
            keyShow,
            offset,
            loading,
            totalRows,
            rows,
            page,
            sortField,
            sortOrder,
            modalDatosFaltantes,
            yearOptions,
            secretaryOptions,
            datosValidos,
            datosFaltantes,
            loadingAsignar,
            modalSuccess,

            // computed
            getLimitValue,
            
            //methods
            getDenunciasRecibidas,
            handlePageChange,
            getClass,
            handleSort,
            aceptarActuacion,
            formatDoc,
            booleans,
            isOfflineDoc,
            subirActuacion,
            OpenModalAddNsum,
            visibleButtons,
            getItems,
            vincularActuacion,
            handleSendDatosFaltantes,
            closeModal,
        }
    }
})
</script>

<style>
    .custom-action-button {
        display: flex;
        width: 124px;
        height: 36px;
        padding: 12px 24px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
        border-radius: 6px;
        background-color: var(--Primary, #0B5195);
        color: white;
        border: none;
    }
    
    .custom-action-button:hover {
        opacity: 0.9;
        cursor: pointer;
    }

    .cancelar-button {
        display: flex;
        width: 124px;
        height: 36px;
        padding: 12px 24px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
        border-radius: 6px;
        background: var(--White-White, #FFF);
        color: #0B5195;
        border: none;
        box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.40);
    }

    .cancelar-button:hover{
        background: #f4f4f4 !important;
        color: #0B5195 !important;
        opacity: 0.9;
        cursor: pointer;
    }

    .blue-line {
        height: 4px;
        background-color: #0B5195;
        border-radius: 9999px;
    }

</style>